import { useId, type ReactNode } from 'react';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { Badge, Button, Panel } from '@/components/ui';

/**
 * The filter bar every Management Dashboard tab wears.
 *
 * Four analytical tabs were built in parallel and each grew its own control
 * strip: one used a dropdown for currency where three used segmented buttons,
 * one stacked eight controls in a single wall, one hid its ordering behind a
 * table heading. The owner's brief is that these read as ONE dashboard and that
 * nothing on screen is there unless it earns its place — so the SHAPE of the
 * bar is decided here, once, rather than four times.
 *
 * ## 🔴 AN ACTIVE FILTER MAY NEVER BE INVISIBLE
 *
 * This is the whole reason the advanced half is a disclosure and not simply a
 * second row that got hidden. A reader looking at a narrowed table who cannot
 * see WHAT narrowed it will read the result as the data — and on a revenue
 * dashboard that is somebody quoting a filtered figure as the business's
 * revenue.
 *
 * So `activeAdvanced` is not decoration:
 *
 * - it renders as a count on the toggle, so a collapsed panel still says how
 *   many filters are in force;
 * - the panel **opens itself** when any advanced filter is set and the reader
 *   has not deliberately closed it;
 * - **Clear** is offered only when something is actually set, so the control
 *   doubles as a statement that something IS set.
 *
 * ⚠️ **The count is the CALLER'S to compute**, because only the caller knows
 * which of its values count as "set" — an empty search box and a region of
 * "all" are defaults, not filters. Pass the number of advanced controls that
 * differ from their default, and nothing else.
 *
 * ## What belongs in each half
 *
 * **Primary** — the controls that change what the numbers MEAN, and which a
 * reader must be able to see without opening anything. On these tabs that is
 * the currency (the unit of every figure) and the period being looked at.
 *
 * **Advanced** — the controls that narrow a population already defined:
 * search, region, ordering, thresholds. Useful, and not needed to read the
 * figure in front of you.
 *
 * ⚠️ **A control whose value changes the UNIT of a figure is PRIMARY, always.**
 * Currency behind a disclosure is how a reader comes to quote dollars as
 * pounds.
 */
export function DashboardFilterBar({
  primary,
  advanced,
  activeAdvanced = 0,
  onClear,
  note,
  rememberAs,
}: {
  /**
   * Filter-memory id for the disclosure's open/closed choice (unique per
   * page). Omitted = not remembered.
   */
  rememberAs?: string;
  /** Always visible. The controls that decide what the figures mean. */
  primary: ReactNode;
  /**
   * Behind the disclosure. Omit entirely on a tab that has none — the toggle
   * then does not render, rather than opening onto nothing.
   */
  advanced?: ReactNode;
  /**
   * How many ADVANCED controls differ from their default. See the header: this
   * is what stops a hidden filter being a silent one.
   */
  activeAdvanced?: number;
  /** Resets the advanced half. Rendered only while something is set. */
  onClear?: () => void;
  /**
   * One line about where the filtering happens, when it is not all one place.
   * Real example: *"The company search is applied by the server. Every other
   * control narrows what came back."* A reader who does not know that cannot
   * explain why one control refetches and the others do not.
   */
  note?: ReactNode;
}) {
  const panelId = useId();
  /*
   * ⚠️ **`null` is "the reader has not decided", and it is a THIRD state rather
   * than a spelling of false.** While it is null the panel follows the filters
   * — open when any is set. Once somebody presses the toggle their choice wins
   * and stays won, including a deliberate close over an active filter, which
   * the count on the toggle then has to carry alone.
   */
  const [override, setOverride] = useRememberedState<boolean | null>(
    rememberAs ?? null,
    null,
    (raw) => (raw === null || typeof raw === 'boolean' ? raw : undefined),
  );
  const hasAdvanced = advanced !== undefined;
  const open = override ?? activeAdvanced > 0;

  return (
    <Panel className="min-w-0">
      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          {primary}

          {hasAdvanced && (
            /*
             * `ml-auto` so the disclosure sits at the far end of the row rather
             * than in the flow of the controls — it is not a filter, it is the
             * door to more of them, and reading as a fourth field is what makes
             * a bar feel like a wall.
             */
            <div className="ml-auto flex items-end gap-2">
              {activeAdvanced > 0 && onClear !== undefined && (
                <Button size="sm" variant="ghost" onClick={onClear}>
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                variant={activeAdvanced > 0 ? 'secondary' : 'ghost'}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOverride(!open)}
              >
                <span className="flex items-center gap-1.5">
                  Filters
                  {/*
                    ⚠️ The count renders whether the panel is open or shut. Open,
                    it confirms what the reader can already see; SHUT, it is the
                    only thing standing between them and a narrowed figure read
                    as the whole.
                  */}
                  {activeAdvanced > 0 && (
                    <Badge tone="neutral">{activeAdvanced}</Badge>
                  )}
                  <span aria-hidden="true" className="text-fg-muted">
                    {open ? '▴' : '▾'}
                  </span>
                </span>
              </Button>
            </div>
          )}
        </div>

        {/*
          ⚠️ **Unmounted when closed, not merely hidden.** A `display: none`
          control keeps its DOM node, stays reachable by a screen reader in some
          combinations, and keeps its `id` in the document — three ways for a
          collapsed panel to behave as though it were open.
        */}
        {hasAdvanced && open && (
          <div
            id={panelId}
            className="flex flex-wrap items-end gap-x-4 gap-y-3 border-t border-border pt-3"
          >
            {advanced}
          </div>
        )}

        {note !== undefined && <p className="text-xs text-fg-muted">{note}</p>}
      </div>
    </Panel>
  );
}

/**
 * A labelled slot for one control in the bar.
 *
 * ⚠️ **A plain `<span>` label, not `Field`.** `Field` emits a `<label for>` and
 * threads an `id`, which is right for one focusable input and WRONG for a
 * `SegmentedControl` — that renders a group of buttons with no single thing to
 * point at, so a `Field` around it binds a label to nothing. This wrapper suits
 * both, and the segmented controls on these tabs carry their own `aria-label`
 * saying the same word as the visible one.
 */
export function FilterSlot({
  label,
  children,
  className = '',
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
      <span className="text-xs font-medium text-fg-secondary">{label}</span>
      {children}
    </div>
  );
}
